import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { urlDeeplinkRegex } from 'omega-rtc/components/form/trident-campaigns/constants/rules';
import { urlPatternRule, urlRule } from 'omega-rtc/constants/rules';
import {
  DYNAMIC_ATTRIBUTES_VALUES,
  MAX_NUMBER_OF_PERMUTATIONS,
  MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN,
  PUSH_CHANNEL_ACTIONS,
  SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG,
  SMART_CAMPAIGN_CHANNELS,
  SMART_CAMPAIGN_MIN_START_DATE,
} from 'omega-rtc/constants/ucd';
import {
  getActionTypeAndURLFromMetaDataPayload,
  getTheNumberOfPermutations,
} from 'omega-rtc/utils/smart-campaign-utils';
import { showControlGroup } from 'omega-rtc/config';

dayjs.extend(customParseFormat);

/**
 * Function used to check the text is valid or not
 * @param text  as a string
 * @returns boolean
 */
const isValidAttribute = (text) => {
  // Check for single brackets of any type eg: { persona.passenger_name }
  const hasSingleBrackets = /(?<!{){(?!{)|(?<!})}(?!})/.test(text);
  if (hasSingleBrackets) {
    return false;
  }

  // Check for correct spacing after {{ and before }} eg: {{persona.passenger_name}}
  const incorrectSpacing = /{{[^ ]|[^ ]}}/.test(text);
  if (incorrectSpacing) {
    return false;
  }

  // Check for every double bracket opened should be closed eg: "{{ persona.passenger_name"
  const hasBalancedBrackets =
    (text.match(/{{/g) || []).length === (text.match(/}}/g) || []).length;
  if (!hasBalancedBrackets) {
    return false;
  }

  // Check for text having number of brackets with space eg: {{ {{persona.passenger_name}} }}
  const hasNumberofBracketsWithSpace = /{{\s{+|}\s}}+/.test(text);
  if (hasNumberofBracketsWithSpace) {
    return false;
  }

  // Check the tokens are matching with the supported user attributes (DYNAMIC_ATTRIBUTES_VALUES)
  const tokens = text.match(/{{\s*[^{}]+\s*}}/g) || [];
  const isInvalidToken = tokens.some((token) => {
    const trimmedToken = token.trim();
    return !Object.values(DYNAMIC_ATTRIBUTES_VALUES).includes(trimmedToken);
  });
  if (isInvalidToken) {
    return false;
  }

  return true;
};

const countChosenObjectsAndValidateTitleContent = (data) => {
  let count = 0;
  let isTitleAndContentFilled = true;
  let isActionURLValid = true;
  let isControlledGroupSelected = false;
  let isContentsDuplicated = false;
  let isValidPersonalAttribute = true;

  let pciDataStatus = {
    isCategoryFilled: true,
    isButtonLinkValid: true,
    isInboxTitleFilled: true,
    isInboxMessageFilled: true,
  };

  if (!data || !data.contents || !data.contents.length) {
    return count;
  }

  // Smart campaign supports only one channel - need to refactor this, while multi-channel supports in smart campaign.
  const channel = data.contents[0].channel;
  const roundsForChannel = data.contents[0].rounds;
  if (
    !roundsForChannel ||
    !roundsForChannel.length ||
    !roundsForChannel[0].variants
  ) {
    return count;
  }

  const selectedContentsBasedOnLanguage = {};

  roundsForChannel.forEach((round) => {
    round.variants.forEach((variant) => {
      if (variant.isChosen === true) {
        count += 1;
        if (variant.isControlGroup) {
          isControlledGroupSelected = true;
        }
        if (!(variant.languages && variant.languages.length)) {
          isTitleAndContentFilled = false;
        } else {
          variant.languages.forEach((language) => {
            const languagePayload = language.payload;
            const languageCode = language.language;
            let title = '';
            let message = '';

            if (languagePayload) {
              // TODO: Validation should happen only if channel == push
              const { actionType, actionURL } =
                getActionTypeAndURLFromMetaDataPayload(languagePayload);
              if (
                channel === SMART_CAMPAIGN_CHANNELS.push &&
                !validatePushChannelActionURL({ actionType, actionURL })
              ) {
                isActionURLValid = false;
              }
            }

            // Validate Payload based on channel:
            switch (channel) {
              case SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG.push: {
                const isContentFilled =
                  languagePayload &&
                  languagePayload.title &&
                  languagePayload.message;
                title = languagePayload && languagePayload.title;
                message = languagePayload && languagePayload.message;
                if (!isContentFilled) {
                  isTitleAndContentFilled = false;
                }
                break;
              }

              case SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG.pci: {
                const { status, isTitleAndMessageFilled } =
                  validatePCIDataStatus(languagePayload);
                title = languagePayload && languagePayload.push_title;
                message = languagePayload && languagePayload.push_message;
                if (Object.values(status).some((v) => !v)) {
                  pciDataStatus = status;
                }
                if (!isTitleAndMessageFilled) {
                  isTitleAndContentFilled = false;
                }
                break;
              }
              default: {
                break;
              }
            }
            const isTitleValid = isValidAttribute(title);
            const isMessageValid = isValidAttribute(message);

            if (!isTitleValid || !isMessageValid) {
              isValidPersonalAttribute = false;
            }

            if (!selectedContentsBasedOnLanguage[languageCode]) {
              selectedContentsBasedOnLanguage[languageCode] = {
                titleWithMessageList: [],
              };
            }

            const combinedTitleAndMessage = `${(title || '').trim()}|${(message || '').trim()}`;
            const combinedTitleAndMessageExistingList =
              selectedContentsBasedOnLanguage[languageCode]
                .titleWithMessageList;
            selectedContentsBasedOnLanguage[languageCode].titleWithMessageList =
              [combinedTitleAndMessage, ...combinedTitleAndMessageExistingList];
          });
        }
      }
    });
  });

  // Looping to find whether duplicate contents exist.
  Object.values(selectedContentsBasedOnLanguage).forEach((content) => {
    if (isContentsDuplicated) {
      return;
    }

    const titleWithMessageList = content.titleWithMessageList;
    if (titleWithMessageList.length !== new Set(titleWithMessageList).size) {
      isContentsDuplicated = true;
    }
  });

  return {
    count,
    isActionURLValid,
    isTitleAndContentFilled,
    isControlledGroupSelected,
    isContentsDuplicated,
    isValidPersonalAttribute,
    pciDataStatus:
      channel !== SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG.pci
        ? {}
        : pciDataStatus,
  };
};

const validatePCIDataStatus = (payload) => {
  const pciDataStatus = {
    isCategoryFilled: false,
    isInboxTitleFilled: false,
    isInboxMessageFilled: false,
    isButtonLinkValid: true,
  };

  if (!payload) {
    return {
      pciDataStatus,
      isTitleAndMessageFilled: false,
    };
  }

  if (payload.inbox_button_link) {
    pciDataStatus.isButtonLinkValid = urlPatternRule().test(
      payload.inbox_button_link,
    );
  }

  const isTitleAndMessageFilled =
    !!payload.push_title && !!payload.push_message;

  pciDataStatus.isCategoryFilled = !!payload.inbox_category;
  pciDataStatus.isInboxTitleFilled = !!payload.inbox_message_title;
  pciDataStatus.isInboxMessageFilled = !!payload.inbox_message_content;

  return {
    status: pciDataStatus,
    isTitleAndMessageFilled,
  };
};

export const validatePushChannelActionURL = ({ actionType, actionURL }) => {
  switch (actionType) {
    case PUSH_CHANNEL_ACTIONS.grabApp:
      return true;
    case PUSH_CHANNEL_ACTIONS.deepLink:
      return urlDeeplinkRegex().test(actionURL);
    case PUSH_CHANNEL_ACTIONS.webUrl:
      return urlRule().test(actionURL);
    default:
      return true;
  }
};

const validateSmartCommsCampaignPayload = (payload) => {
  const campaignDetails = payload.campaign;
  let formErrors = {};

  if (!campaignDetails || !Object.keys(campaignDetails).length) {
    formErrors = {
      approver: 'Please select a approver',
      name: 'Please enter a campaign name',
      verticals: 'Please select a vertical',
      start: 'Please enter a valid start date',
      end: 'Please enter a valid end date',
      targetSegments: 'Please select a user namespace',
      smartCommsContentMetadata: 'Please select at least 5 content variants',
    };

    return formErrors;
  }

  // Project
  if (!payload.projectID) {
    formErrors.projectID = 'Please select a project';
  }

  // Approver
  if (!campaignDetails.approver) {
    formErrors.approver = 'Please select a approver';
  }

  // Campaign name
  if (!campaignDetails.name) {
    formErrors.name = 'Please enter a campaign name';
  }

  // Verticals
  if (!campaignDetails.verticals || !campaignDetails.verticals.length) {
    formErrors.verticals = 'Please select a vertical';
  }

  // Dates
  if (
    !campaignDetails.start ||
    !dayjs(campaignDetails.start, 'YYYY-MM-DD', true).isValid()
  ) {
    formErrors.start = 'Please enter a valid start date';
  }
  if (
    !campaignDetails.end ||
    !dayjs(campaignDetails.end, 'YYYY-MM-DD', true).isValid()
  ) {
    formErrors.end = 'Please enter a valid end date';
  }
  if (
    dayjs(campaignDetails.start, 'YYYY-MM-DD', true).isValid() &&
    dayjs(campaignDetails.end, 'YYYY-MM-DD', true).isValid()
  ) {
    if (
      dayjs(campaignDetails.start).isBefore(
        SMART_CAMPAIGN_MIN_START_DATE,
        'day',
      )
    ) {
      formErrors.start = 'Campaign start date should be after tomorrow';
    }
    if (
      dayjs(campaignDetails.end).isBefore(dayjs(campaignDetails.start), 'day')
    ) {
      formErrors.end = 'Campaign start date should be before end date';
    }
  }

  // if control group time not between start and end date
  if (showControlGroup) {
    if (
      !campaignDetails.controlGroupTime ||
      !dayjs(campaignDetails.controlGroupTime).isValid() ||
      dayjs(campaignDetails.controlGroupTime).isBefore(
        dayjs(campaignDetails.start).startOf('day'),
      ) ||
      dayjs(campaignDetails.controlGroupTime).isAfter(
        dayjs(campaignDetails.end).endOf('day'),
      )
    ) {
      formErrors.controlGroupTime =
        'Control group time should be between start and end date';
    }
  }

  // Target segments
  if (
    !campaignDetails.targetSegments ||
    !campaignDetails.targetSegments.length
  ) {
    formErrors.targetSegments = 'Please select a user namespace';
  }
  // Content variants and its count along with title + content validations
  const {
    count,
    pciDataStatus,
    isActionURLValid,
    isTitleAndContentFilled,
    isControlledGroupSelected,
    isContentsDuplicated,
    isValidPersonalAttribute,
  } = countChosenObjectsAndValidateTitleContent(
    campaignDetails.smartCommsContentMetadata || {},
  );
  if (campaignDetails.smartCommsContentMetadata) {
    if (count < MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN) {
      formErrors.smartCommsContentMetadata = `Please select at least ${MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN} content variants`;
    } else if (!isTitleAndContentFilled) {
      formErrors.smartCommsContentMetadata =
        'Title and content is required in each language for all selected content variants';
    } else if (!isActionURLValid) {
      formErrors.smartCommsContentMetadata =
        'Failed to save the campaign. Please enter valid action URL';
    } else if (showControlGroup && !isControlledGroupSelected) {
      formErrors.smartCommsContentMetadata =
        'Mark a control group to continue.';
    } else if (isContentsDuplicated) {
      formErrors.smartCommsContentMetadata =
        'Failed to save the campaign. Please remove duplicated contents';
    } else if (!isValidPersonalAttribute) {
      formErrors.smartCommsContentMetadata =
        'Failed to save the campaign. Please select a variant with valid personal attribute.';
    } else if (Object.values(pciDataStatus).some((v) => !v)) {
      // PCI validation
      if (!pciDataStatus.isCategoryFilled) {
        formErrors.smartCommsContentMetadata = 'Please select a inbox category';
      }
      if (!pciDataStatus.isInboxTitleFilled) {
        formErrors.smartCommsContentMetadata = 'Please input inbox title';
      }
      if (!pciDataStatus.isInboxMessageFilled) {
        formErrors.smartCommsContentMetadata = 'Please input inbox message';
      }
      if (!pciDataStatus.isButtonLinkValid) {
        formErrors.smartCommsContentMetadata =
          'Please enter a valid button link';
      }
    }
  }

  //  campaign size validation based on the number of permutations
  if (
    campaignDetails &&
    campaignDetails.smartCommsMetadata &&
    campaignDetails.smartCommsMetadata.segmentSize
  ) {
    const campaignDurationDays = dayjs(campaignDetails.endTime).diff(
      dayjs(campaignDetails.startTime),
      'day',
    );
    const numberOfPermutations = getTheNumberOfPermutations({
      campaignDurationDays,
      numberOfContentVariants: count,
      segmentSize: campaignDetails.smartCommsMetadata.segmentSize,
    });
    if (numberOfPermutations > MAX_NUMBER_OF_PERMUTATIONS) {
      formErrors.permutationError =
        'Unable to submit the campaign as it exceeds the max number of permutations';
    }
  }

  return formErrors;
};

export {
  countChosenObjectsAndValidateTitleContent,
  validateSmartCommsCampaignPayload,
};
